import { requireService, wrap } from '@repo/di-ez';
import {
  CheckRingLight,
  CloseSquareLight,
  DoneLight,
  EditLight,
  SettingLineLight,
  TableLight,
  TrashLight,
  UploadAlt,
} from '@repo/go-icons';
import { Button, Checkbox, DatePickerV2 } from '@repo/go-ui';
import { useTranslation } from 'react-i18next';

import { useStore } from 'src/store';

import styles from './control-bar.module.scss';

export const ControlBar = wrap(function ControlBar() {
  const { editing } = useStore();
  const { t } = useTranslation();
  const wellsService = requireService('wellsService');
  const wellsPageStore = requireService('wellsPageStore');

  // Если у пользователя нет прав на редактирование скважин, страница должна оставаться
  // в readonly-режиме, даже если режим редактирования был включён на другой странице.
  const isWellsEditingActive = editing.isEditing && wellsPageStore.isEditingAvailable;

  return (
    <div className={styles.wrapper}>
      <div className={styles.leftBlockWrapper}>
        <Checkbox
          disabled={!wellsPageStore.isPlannedWellsChecked && !wellsPageStore.isUnplannedWellsChecked}
          checked={wellsPageStore.isDrilledWellsChecked}
          onChange={() => wellsPageStore.setIsDrilledWellsChecked(!wellsPageStore.isDrilledWellsChecked)}
        >
          <p className={styles.label}>{t('wells:drilled')}</p>
        </Checkbox>
        <Checkbox
          disabled={!wellsPageStore.isDrilledWellsChecked && !wellsPageStore.isUnplannedWellsChecked}
          checked={wellsPageStore.isPlannedWellsChecked}
          onChange={() => wellsPageStore.setIsPlannedWellsChecked(!wellsPageStore.isPlannedWellsChecked)}
        >
          <p className={styles.label}>{t('wells:planned')}</p>
        </Checkbox>
        <Checkbox
          disabled={!wellsPageStore.isDrilledWellsChecked && !wellsPageStore.isPlannedWellsChecked}
          checked={wellsPageStore.isUnplannedWellsChecked}
          onChange={() => wellsPageStore.setIsUnplannedWellsChecked(!wellsPageStore.isUnplannedWellsChecked)}
        >
          <p className={styles.label}>{t('wells:unplanned')}</p>
        </Checkbox>
        <div className={styles.datePickerWrapper}>
          <DatePickerV2
            className={styles.datePicker}
            size="m"
            placeholder={t('common:startDate')}
            value={wellsPageStore.startDate}
            onChange={wellsPageStore.setStartDate}
          />
          –
          <DatePickerV2
            className={styles.datePicker}
            size="m"
            placeholder={t('common:endDate')}
            value={wellsPageStore.endDate}
            isDateDisabled={(date) => date < (wellsPageStore.startDate ?? 0)}
            onChange={wellsPageStore.setEndDate}
          />
        </div>
      </div>
      <div className={styles.rightBlockWrapper}>
        {!isWellsEditingActive && wellsPageStore.isEditingAvailable && (
          <Button
            variant="flat"
            size="m"
            icon={<EditLight />}
            style={{ color: 'var(--go-text)', width: 24, height: 24 }}
            title={t('common:edit')}
            onClick={editing.enableEditing}
          />
        )}
        {isWellsEditingActive && (
          <>
            <Button
              variant="danger"
              size="m"
              icon={<TrashLight />}
              disabled={
                (typeof wellsPageStore.isDeleteButtonDisabled === 'object'
                  ? wellsPageStore.isDeleteButtonDisabled.disabled
                  : wellsPageStore.isDeleteButtonDisabled) || wellsService.isWellsUpdating
              }
              title={
                typeof wellsPageStore.isDeleteButtonDisabled === 'object'
                  ? wellsPageStore.isDeleteButtonDisabled.description
                  : t('common:Buttons.delete')
              }
              onClick={wellsPageStore.onDeleteWells}
            />
            <Button
              variant="danger"
              size="m"
              icon={<CloseSquareLight />}
              title={t('common:Buttons.cancel')}
              disabled={wellsService.isWellsUpdating}
              onClick={editing.cancelEditing}
            >
              {t('common:Buttons.cancel')}
            </Button>
            <Button
              variant="success"
              size="m"
              icon={<DoneLight />}
              title={t('common:Buttons.save')}
              disabled={wellsService.isWellsUpdating}
              onClick={() => editing.saveChanges()}
            >
              {t('common:Buttons.save')}
            </Button>
          </>
        )}
        <Button
          disabled={!wellsPageStore.tableTemplatesStore.isTemplatesInitiated}
          variant="flat"
          size="m"
          icon={<TableLight />}
          style={{ color: 'var(--go-text)', width: 24, height: 24 }}
          title={t('wells:ControlBar.openTemplatesList')}
          onClick={() => wellsPageStore.setIsTableTemplatesOpen(true)}
        />
        {!isWellsEditingActive && (
          <Button
            variant={wellsPageStore.isTableCheckboxesOn ? 'primary' : 'flat'}
            icon={<CheckRingLight />}
            size="m"
            title={t('wells:ControlBar.showCheckboxes')}
            style={{ color: 'var(--go-text)', width: 24, height: 24 }}
            onClick={() => wellsPageStore.toggleTableCheckboxes()}
          />
        )}
        <Button
          disabled={!wellsPageStore.tableTemplatesStore.isTemplatesInitiated}
          variant="flat"
          size="m"
          loading={wellsPageStore.isExporting}
          icon={<UploadAlt />}
          style={{ color: 'var(--go-text)' }}
          title={t('wells:ControlBar.exportTableToExcel')}
          onClick={() => wellsPageStore.setIsExportTemplatesOpen(true)}
        />
        <Button
          variant="flat"
          size="m"
          icon={<SettingLineLight />}
          style={{ color: 'var(--go-text)' }}
          title={t('wells:ControlBar.displaySettings')}
          onClick={() => wellsPageStore.setIsTableSettingsOpen(true)}
        />
      </div>
    </div>
  );
});
